import { useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useQuestHistoryQuery } from 'vvs-gotchi-graphql'
import { QUEST_ITEMS_PER_PAGE } from 'config/constants/vvsgotchi'

import { questHistoryFromGraphqlData } from '../models'
import getQuestConfigs from '../questConfigs'

const useQuestHistoryRecords = () => {
  const { account } = useActiveWeb3React()
  const { t } = useTranslation()
  const questConfigs = useMemo(() => getQuestConfigs(t), [t])

  const { data, loading, fetchMore } = useQuestHistoryQuery({
    skip: !account,
    variables: {
      address: account,
      first: QUEST_ITEMS_PER_PAGE,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })

  const records = useMemo(
    () =>
      account && data?.owner
        ? data.owner.questHistory.questHistoryConnection.edges
            .map(({ node }) => questHistoryFromGraphqlData(node))
            .map((history) => {
              const questConfig = questConfigs[history.questSymbol]
              return { history, questConfig }
            })
            .filter(({ questConfig }) => questConfig)
            .map(({ history, questConfig }, index) => ({
              index,
              ...history,
              ...questConfig,
            }))
        : [],
    [data, account, questConfigs],
  )

  const { pageInfo, totalCount } = data?.owner?.questHistory.questHistoryConnection ?? {}
  const pageInfoRef = useRef(pageInfo)
  pageInfoRef.current = pageInfo
  const fetchNext = useCallback(async () => {
    const { hasNextPage, endCursor } = pageInfoRef.current ?? {}
    if (account && hasNextPage) {
      try {
        await fetchMore({
          variables: {
            after: endCursor || null,
          },
        })
      } catch (err) {
        console.error(err)
      }
    }
  }, [account, fetchMore])

  return {
    records,
    fetchNext,
    loading,
    fetchedPage: Math.floor(records.length / QUEST_ITEMS_PER_PAGE) + (pageInfo?.hasNextPage ? 0 : 1),
    totalPages: Math.ceil(totalCount / QUEST_ITEMS_PER_PAGE) || 0,
  }
}

export default useQuestHistoryRecords
